import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity} from 'react-native';
import {Text, Divider, Snackbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {paymentApi} from '../../services/api/paymentApi';

const PayoutCard = ({item}) => {
  const [expanded, setExpanded] = useState(false);
  const items = item.items || [];
  const hasItems = items.length > 0;

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'});
  };

  const formatMode = mode => {
    const modes = {CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank Transfer', CHEQUE: 'Cheque'};
    return modes[mode] || mode;
  };

  return (
    <View style={styles.card}>
      <View style={styles.cardRow}>
        <Text style={styles.txnCode}>{item.transactionCode}</Text>
        <Text style={styles.amount}>{'\u20B9'}{item.totalAmount}</Text>
      </View>
      <View style={styles.cardRow}>
        <Text style={styles.workerName}>{item.payeeName}</Text>
        <Text style={styles.date}>{formatDate(item.paymentDate)}</Text>
      </View>
      <View style={styles.cardRow}>
        <Text style={styles.modeBadge}>{formatMode(item.paymentMode)}</Text>
        {hasItems ? (
          <TouchableOpacity
            style={styles.itemCountBtn}
            onPress={() => setExpanded(prev => !prev)}
            activeOpacity={0.7}>
            <Text style={styles.itemCountText}>
              {item.itemCount} item{item.itemCount !== 1 ? 's' : ''}
            </Text>
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={colors.primary}
            />
          </TouchableOpacity>
        ) : (
          <Text style={styles.itemCount}>{item.itemCount} item{item.itemCount !== 1 ? 's' : ''}</Text>
        )}
      </View>
      {expanded && hasItems && (
        <View style={styles.expandedSection}>
          <View style={styles.expandedHeader}>
            <Text style={[styles.expandedHeaderText, {flex: 0.5}]}>Type</Text>
            <Text style={[styles.expandedHeaderText, {flex: 2}]}>Description</Text>
            <Text style={[styles.expandedHeaderText, {flex: 1.5}]}>Order#</Text>
            <Text style={[styles.expandedHeaderText, {flex: 1, textAlign: 'right'}]}>Amount</Text>
          </View>
          {items.map((pi, idx) => {
            const isTask = pi.type === 'Task';
            return (
              <View key={pi.id || idx} style={[styles.expandedRow, idx % 2 === 0 && styles.expandedRowAlt]}>
                <View style={{flex: 0.5}}>
                  <View style={[styles.typeBadge, {backgroundColor: isTask ? colors.primary + '25' : '#FF980025'}]}>
                    <Text style={[styles.typeBadgeText, {color: isTask ? colors.primary : '#FF9800'}]}>
                      {isTask ? 'T' : 'D'}
                    </Text>
                  </View>
                </View>
                <Text style={[styles.expandedCell, {flex: 2}]} numberOfLines={1}>
                  {pi.taskTypeName || (isTask ? 'Task' : 'Dress Assignment')}
                </Text>
                <Text style={[styles.expandedCell, {flex: 1.5}]} numberOfLines={1}>
                  {pi.orderNumber || '-'}
                </Text>
                <Text style={[styles.expandedCell, {flex: 1, textAlign: 'right', fontWeight: '600'}]}>
                  {'\u20B9'}{pi.amount}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const WorkerPayoutsScreen = () => {
  const {t} = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  const fetchPayouts = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const res = await paymentApi.getHistory();
      setPayouts(res.data?.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to load payouts';
      setSnackbar({visible: true, message: msg});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchPayouts();
    }, [fetchPayouts]),
  );

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPayouts(false);
  };

  if (loading && payouts.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Divider />

      <FlatList
        data={payouts}
        keyExtractor={item => String(item.id)}
        renderItem={({item}) => <PayoutCard item={item} />}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        contentContainerStyle={payouts.length === 0 ? styles.emptyListContent : undefined}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>{t('worker.noPayouts')}</Text>
          </View>
        }
      />

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  centerContent: {justifyContent: 'center', alignItems: 'center'},
  emptyListContent: {flex: 1},
  card: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  txnCode: {fontSize: 14, fontWeight: '700', color: colors.text},
  amount: {fontSize: 15, fontWeight: '700', color: colors.primary},
  workerName: {fontSize: 13, color: colors.text},
  date: {fontSize: 13, color: colors.textSecondary},
  modeBadge: {
    fontSize: 12,
    color: colors.textSecondary,
    backgroundColor: colors.background,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  itemCount: {fontSize: 12, color: colors.textSecondary},
  itemCountBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 4,
    gap: 2,
  },
  itemCountText: {fontSize: 12, color: colors.primary, fontWeight: '600'},
  // Expanded section
  expandedSection: {
    marginTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: spacing.sm,
  },
  expandedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  expandedHeaderText: {fontSize: 11, fontWeight: '700', color: colors.textSecondary},
  expandedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
  },
  expandedRowAlt: {backgroundColor: '#FAFAFA'},
  expandedCell: {fontSize: 12, color: colors.text},
  typeBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadgeText: {fontSize: 10, fontWeight: '700'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 4},
  emptyText: {fontSize: 16, color: colors.textSecondary},
});

export default WorkerPayoutsScreen;
